/**
 * NotebookContextMenu — Notion-style right-click menu on the editor.
 *
 * Triggered by a `contextmenu` event on the editor's contenteditable.
 * Suppresses the browser menu and renders a positioned palette with
 * block-level actions:
 *   - Turn into (paragraph / h1-h3 / lists / quote / code)
 *   - Duplicate block
 *   - Delete block
 *   - Copy link to block (deferred — needs stable block ids, Phase 7)
 *
 * Rendered via React portal to `document.body` so the menu's fixed-
 * positioned nodes never get tangled in the editor's overflow /
 * scrolling context (which otherwise triggers React "insertBefore"
 * reconciliation errors when the editor's DOM subtree re-mounts).
 *
 * The current top-level block is resolved from the cursor's resolved
 * position; actions operate on that block's `from`/`to` range.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { Editor } from '@tiptap/react'
import { turnIntoTable } from '@/lib/tiptap/tableExtension'
import { cn } from '@/lib/utils'

export interface NotebookContextMenuProps {
  editor: Editor | null
  /** Anchor point in viewport coords. Null = menu closed. */
  anchor: { x: number; y: number } | null
  onClose: () => void
}

interface MenuPos {
  top: number
  left: number
}

const VIEWPORT_GUTTER = 8
const MENU_WIDTH = 200
const MENU_MAX_HEIGHT = 320

export function NotebookContextMenu({ editor, anchor, onClose }: NotebookContextMenuProps) {
  const [pos, setPos] = useState<MenuPos | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const [turnIntoOpen, setTurnIntoOpen] = useState(false)

  useLayoutEffect(() => {
    if (!anchor) {
      setPos(null)
      return
    }
    const vw = window.innerWidth
    const vh = window.innerHeight
    const left = Math.min(anchor.x, vw - MENU_WIDTH - VIEWPORT_GUTTER)
    const top = Math.min(anchor.y, vh - MENU_MAX_HEIGHT - VIEWPORT_GUTTER)
    setPos({ top: Math.max(VIEWPORT_GUTTER, top), left: Math.max(VIEWPORT_GUTTER, left) })
  }, [anchor])

  useEffect(() => {
    if (!anchor) return
    const onScroll = () => onClose()
    window.addEventListener('scroll', onScroll, true)
    return () => window.removeEventListener('scroll', onScroll, true)
  }, [anchor, onClose])

  if (!anchor || !editor) return null

  const { state } = editor
  const $from = state.selection.$from
  // Walk up to the top-level block. In ProseMirror the document root sits
  // at depth 0, so the first top-level block (paragraph / heading / list /
  // code block / quote) is at depth 1. We must NOT call $from.before(0) —
  // that throws "There is no position before the top-level node" because
  // there is no "before" the document root.
  const topDepth = $from.depth >= 1 ? 1 : 0
  const $block = $from.node(topDepth)
  const blockStart = topDepth > 0 ? $from.before(topDepth) : $from.pos
  const blockEnd = topDepth > 0 ? $from.after(topDepth) : $from.pos
  const blockRange = { from: blockStart, to: blockEnd }

  const blockTypeLabel = labelFor($block?.type.name)

  const isInCodeBlock = editor.isActive('codeBlock')

  const duplicate = () => {
    if ($block) {
      const slice = state.doc.slice(blockStart, blockEnd)
      const json = slice.toJSON()
      editor
        .chain()
        .focus()
        .insertContentAt(blockEnd, json.content ?? [])
        .run()
    }
    onClose()
  }

  const deleteBlock = () => {
    editor.chain().focus().deleteRange(blockRange).run()
    onClose()
  }

  const turnInto = (run: () => void) => {
    run()
    onClose()
  }

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[60]"
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault()
          onClose()
        }}
      />
      <div
        ref={menuRef}
        role="menu"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault()
            onClose()
          }
        }}
        className={cn(
          'z-[70] overflow-hidden rounded-md border border-surface-200 bg-white py-1 shadow-xl shadow-black/10',
          'dark:border-surface-700 dark:bg-surface-800',
        )}
        style={{
          position: 'fixed',
          top: pos?.top ?? anchor.y,
          left: pos?.left ?? anchor.x,
          width: MENU_WIDTH,
          maxHeight: MENU_MAX_HEIGHT,
          overflowY: 'auto',
        }}
      >
        <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-surface-400">
          {blockTypeLabel}
        </div>

        <Item
          icon="↩"
          label="Turn into"
          onClick={() => setTurnIntoOpen(true)}
          hasSubmenu
        />
        {turnIntoOpen && (
          <div className="border-y border-surface-100 bg-surface-50/60 py-1 dark:border-surface-700/60 dark:bg-surface-900/40">
            {[
              { label: 'Text', run: () => editor.chain().focus().setParagraph().run() },
              { label: 'Heading 1', run: () => editor.chain().focus().toggleHeading({ level: 1 }).run() },
              { label: 'Heading 2', run: () => editor.chain().focus().toggleHeading({ level: 2 }).run() },
              { label: 'Heading 3', run: () => editor.chain().focus().toggleHeading({ level: 3 }).run() },
              { label: 'Bulleted list', run: () => editor.chain().focus().toggleBulletList().run() },
              { label: 'Numbered list', run: () => editor.chain().focus().toggleOrderedList().run() },
              { label: 'Todo list', run: () => editor.chain().focus().toggleTaskList().run() },
              { label: 'Quote', run: () => editor.chain().focus().toggleBlockquote().run() },
              { label: 'Code block', run: () => editor.chain().focus().toggleCodeBlock().run() },
              { label: 'Table', run: () => turnIntoTable(editor) },
            ].map((it) => (
              <button
                key={it.label}
                type="button"
                onClick={() => turnInto(it.run)}
                className="flex w-full items-center px-6 py-1.5 text-left text-xs text-surface-700 hover:bg-surface-100 dark:text-surface-200 dark:hover:bg-surface-700"
              >
                {it.label}
              </button>
            ))}
          </div>
        )}

        <Item
          icon="▦"
          label="Table"
          onClick={() => {
            turnIntoTable(editor)
            onClose()
          }}
          disabled={isInCodeBlock}
        />
        <Item icon="⧉" label="Duplicate" onClick={duplicate} disabled={isInCodeBlock} />
        <Item icon="⌫" label="Delete" onClick={deleteBlock} danger disabled={isInCodeBlock} />
      </div>
    </>,
    document.body,
  )
}

function Item({
  icon,
  label,
  onClick,
  hasSubmenu,
  danger,
  disabled,
}: {
  icon: string
  label: string
  onClick: () => void
  hasSubmenu?: boolean
  danger?: boolean
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-surface-100 dark:hover:bg-surface-700',
        danger && 'text-red-600 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-900/30',
        !danger && 'text-surface-700 dark:text-surface-200',
        disabled && 'cursor-not-allowed opacity-40 hover:bg-transparent dark:hover:bg-transparent',
      )}
    >
      <span className="w-4 text-center text-sm leading-none">{icon}</span>
      <span className="flex-1">{label}</span>
      {hasSubmenu && <span className="text-surface-400">▸</span>}
    </button>
  )
}

function labelFor(name: string | undefined): string {
  switch (name) {
    case 'paragraph':
      return 'Text'
    case 'heading':
      return 'Heading'
    case 'bulletList':
      return 'Bulleted list'
    case 'orderedList':
      return 'Numbered list'
    case 'taskList':
      return 'Todo list'
    case 'blockquote':
      return 'Quote'
    case 'codeBlock':
      return 'Code'
    case 'horizontalRule':
      return 'Divider'
    case 'pageLink':
      return 'Page link'
    case 'mention':
      return 'Mention'
    default:
      return name ?? 'Block'
  }
}
